import React, { useState, useEffect } from 'react';
// import { useStudentAuth } from '../../../context/StudentAuthContext';
import { useStudentAuth } from '../../context/StudentAuthContext';
import toast from 'react-hot-toast';

const StudentResultPage = () => {
  const { studentApi, profile } = useStudentAuth();
  const [academicSessions, setAcademicSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState('');
  const [selectedTerm, setSelectedTerm] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [loadingSessions, setLoadingSessions] = useState(true);

  useEffect(() => {
    fetchAcademicSessions();
  }, []);

  const fetchAcademicSessions = async () => {
    try {
      setLoadingSessions(true);
      const response = await studentApi.get('/student-portal/academic-sessions');
      const sessions = response.data || [];
      setAcademicSessions(sessions);
      if (sessions.length > 0) {
        const latestSession = sessions[0];
        setSelectedSession(latestSession.id);
        if (latestSession.terms && latestSession.terms.length > 0) {
          setSelectedTerm(latestSession.terms[0].id);
        }
      }
    } catch (error) {
      toast.error('Failed to load academic sessions');
      console.error(error);
    } finally {
      setLoadingSessions(false);
    }
  };

  const downloadResult = async () => {
    if (!selectedSession || !selectedTerm) {
      toast.error('Please select both session and term');
      return;
    }
    if (!profile?.allow_student_report_print) {
      toast.error('Printing results is not enabled by your school');
      return;
    }
    try {
      setDownloading(true);
      const response = await studentApi.post(
        '/student-portal/print-report',
        {},
        {
          params: {
            academic_session_id: selectedSession,
            term_id: selectedTerm,
          },
          responseType: 'blob',
        }
      );

      // Let the browser handle the download using the filename from the server
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;

      // Try to get filename from Content-Disposition header
      const contentDisposition = response.headers['content-disposition'];
      let filename = `result_${profile?.student_reg || 'student'}.pdf`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (match && match[1]) {
          filename = match[1].replace(/['"]/g, '');
        }
      }
      link.setAttribute('download', filename);

      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Result downloaded successfully');
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to download result';
      toast.error(errorMsg);
    } finally {
      setDownloading(false);
    }
  };

  const getCurrentSession = () => {
    return academicSessions.find((s) => s.id == selectedSession);
  };

  const currentSession = getCurrentSession();
  const currentTerm = currentSession?.terms?.find((t) => t.id == selectedTerm);

  if (loadingSessions) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  const isPrintingAllowed = profile?.allow_student_report_print === true;

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100">
        <div className="bg-gradient-to-r from-indigo-600 to-indigo-700 px-6 py-8 text-white">
          <h1 className="text-2xl font-bold mb-2">Download Result</h1>
          <p className="opacity-90">
            Select the session and term to download your official result report.
            A PDF will be generated and downloaded to your device.
          </p>
        </div>

        <div className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Academic Session
              </label>
              <div className="relative">
                <select
                  value={selectedSession}
                  onChange={(e) => {
                    setSelectedSession(e.target.value);
                    setSelectedTerm('');
                  }}
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent appearance-none"
                >
                  <option value="">Select a session</option>
                  {academicSessions.map((session) => (
                    <option key={session.id} value={session.id}>
                      {session.academic_session}
                    </option>
                  ))}
                </select>
                <div className="absolute right-3 top-3 text-gray-400 pointer-events-none">
                  <i className="fas fa-chevron-down"></i>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Term
              </label>
              <div className="relative">
                <select
                  value={selectedTerm}
                  onChange={(e) => setSelectedTerm(e.target.value)}
                  disabled={!selectedSession}
                  className={`w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent appearance-none ${
                    !selectedSession ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  <option value="">Select a term</option>
                  {currentSession?.terms?.map((term) => (
                    <option key={term.id} value={term.id}>
                      {term.term}
                    </option>
                  ))}
                </select>
                <div className="absolute right-3 top-3 text-gray-400 pointer-events-none">
                  <i className="fas fa-chevron-down"></i>
                </div>
              </div>
            </div>
          </div>

          {!isPrintingAllowed && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 text-yellow-800">
              <div className="flex items-start space-x-2">
                <i className="fas fa-exclamation-triangle mt-0.5"></i>
                <div>
                  <p className="font-medium">Printing Not Enabled</p>
                  <p className="text-sm">
                    Your school has not enabled the option to print result reports from the student portal.
                    Please contact your school administrator if you need a copy of your results.
                  </p>
                </div>
              </div>
            </div>
          )}

          {isPrintingAllowed && (
            <>
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-blue-800 text-sm">
                <div className="flex items-start space-x-2">
                  <i className="fas fa-info-circle mt-0.5"></i>
                  <div>
                    <p className="font-medium">Note:</p>
                    <p>
                      Downloading your result will deduct credit point from the school's account.
                      You can only download each unique result (session + term) once without being charged again.
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  onClick={downloadResult}
                  disabled={downloading || !selectedSession || !selectedTerm}
                  className={`px-6 py-3 rounded-xl transition-all duration-200 flex items-center space-x-2 font-semibold ${
                    !selectedSession || !selectedTerm || downloading
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-green-600 hover:bg-green-700 text-white'
                  }`}
                >
                  {downloading ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i>
                      <span>Generating...</span>
                    </>
                  ) : (
                    <>
                      <i className="fas fa-print"></i>
                      <span>Download Result PDF</span>
                    </>
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default StudentResultPage;